import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Upload, User, Mail, Phone, MapPin, Briefcase, 
  Calendar, DollarSign, Linkedin, FileText, CheckCircle,
  Loader2, AlertCircle, Building2, Search
} from 'lucide-react';
import { toast } from 'sonner';

export default function PublicCVSubmitPage() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  
  // Company search
  const [companySearch, setCompanySearch] = useState('');
  const [companies, setCompanies] = useState([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    current_location: '',
    current_designation: '',
    current_company: '',
    total_years_experience: '',
    skills: '',
    expected_salary: '',
    notice_period_days: '',
    linkedin_url: '',
    professional_summary: ''
  });

  // Load companies when search changes
  useEffect(() => {
    const searchCompanies = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${API_URL}/api/public/companies/search?q=${encodeURIComponent(companySearch)}&limit=10`);
        if (res.ok) {
          const data = await res.json();
          setCompanies(data.companies || []);
        }
      } catch (error) {
        console.error('Failed to search companies:', error);
      }
    };
    
    const debounce = setTimeout(searchCompanies, 300);
    return () => clearTimeout(debounce);
  }, [companySearch]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const selectCompany = (company) => {
    setSelectedCompany(company);
    setFormData({ ...formData, current_company: company.name });
    setCompanySearch(company.name);
    setShowCompanyDropdown(false);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        setCvFile(file);
      }
    }
  };

  const validateFile = (file) => {
    const validTypes = ['.pdf', '.doc', '.docx', '.txt'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!validTypes.includes(ext)) {
      toast.error('Please upload PDF, DOC, DOCX, or TXT file');
      return false;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return false;
    }
    return true;
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        setCvFile(file);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!cvFile && !formData.email) {
      toast.error('Please upload your CV or provide your email');
      return;
    }

    setSubmitting(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const submitData = new FormData();
      
      if (cvFile) {
        submitData.append('cv_file', cvFile);
      }
      
      Object.entries(formData).forEach(([key, value]) => {
        if (value) submitData.append(key, value);
      });

      const response = await fetch(`${API_URL}/api/public/submit-cv`, {
        method: 'POST',
        body: submitData
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success('CV submitted successfully!');
      } else {
        let error;
        try {
          error = await response.json();
        } catch {
          error = { detail: 'Server error. Please try again.' };
        }
        toast.error(error.detail || 'Failed to submit CV');
      }
    } catch (error) {
      toast.error('Failed to submit CV. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Thank You!</h2>
            <p className="text-slate-600 mb-6">
              Your CV has been submitted successfully. Our team will review your profile and contact you if there's a suitable opportunity.
            </p>
            <Button onClick={() => window.location.reload()} variant="outline">
              Submit Another CV
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Submit Your CV</h1>
          <p className="text-slate-600">
            Upload your resume or fill in your details below. We'll get in touch if there's a match!
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* CV Upload */}
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Upload className="w-5 h-5 text-blue-500" />
                Upload Your CV
              </CardTitle>
              <CardDescription>Supported formats: PDF, DOC, DOCX, TXT (Max 10MB)</CardDescription>
            </CardHeader>
            <CardContent>
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  dragActive ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                } ${cvFile ? 'border-green-500 bg-green-50' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                {cvFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="w-8 h-8 text-green-500" />
                    <div className="text-left">
                      <p className="font-medium text-slate-800">{cvFile.name}</p>
                      <p className="text-sm text-slate-500">{(cvFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setCvFile(null)}
                      className="text-red-500 hover:text-red-700"
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-600 mb-2">Drag and drop your CV here</p>
                    <p className="text-sm text-slate-400 mb-4">or</p>
                    <label className="cursor-pointer">
                      <span className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                        Browse Files
                      </span>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.txt"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                    </label>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Manual Entry */}
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-purple-500" />
                Your Details
              </CardTitle>
              <CardDescription>
                {cvFile ? 'Optional - we\'ll extract details from your CV' : 'Fill in your information manually'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="flex items-center gap-1 mb-1.5">
                    <User className="w-3.5 h-3.5" /> Full Name
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="flex items-center gap-1 mb-1.5">
                    <Mail className="w-3.5 h-3.5" /> Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    required={!cvFile}
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="flex items-center gap-1 mb-1.5">
                    <Phone className="w-3.5 h-3.5" /> Phone
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 9876543210"
                  />
                </div>
                <div>
                  <Label htmlFor="current_location" className="flex items-center gap-1 mb-1.5">
                    <MapPin className="w-3.5 h-3.5" /> Current Location
                  </Label>
                  <Input
                    id="current_location"
                    name="current_location"
                    value={formData.current_location}
                    onChange={handleChange}
                    placeholder="Mumbai, India"
                  />
                </div>
                <div>
                  <Label htmlFor="current_designation" className="flex items-center gap-1 mb-1.5">
                    <Briefcase className="w-3.5 h-3.5" /> Current Designation
                  </Label>
                  <Input
                    id="current_designation"
                    name="current_designation"
                    value={formData.current_designation}
                    onChange={handleChange}
                    placeholder="Software Engineer"
                  />
                </div>
                <div>
                  <Label htmlFor="total_years_experience" className="flex items-center gap-1 mb-1.5">
                    <Calendar className="w-3.5 h-3.5" /> Years of Experience
                  </Label>
                  <Input
                    id="total_years_experience"
                    name="total_years_experience"
                    type="number"
                    step="0.5"
                    value={formData.total_years_experience}
                    onChange={handleChange}
                    placeholder="5"
                  />
                </div>
              </div>

              {/* Current Company with Search */}
              <div className="relative">
                <Label htmlFor="current_company" className="flex items-center gap-1 mb-1.5">
                  <Building2 className="w-3.5 h-3.5" /> Current Company
                </Label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="current_company"
                    value={companySearch}
                    onChange={(e) => {
                      setCompanySearch(e.target.value);
                      setShowCompanyDropdown(true);
                      setSelectedCompany(null);
                    }}
                    onFocus={() => setShowCompanyDropdown(true)}
                    placeholder="Search or type company name..."
                    className="pl-10"
                  />
                </div>
                {showCompanyDropdown && companies.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {companies.map((company) => (
                      <button
                        key={company.id}
                        type="button"
                        onClick={() => selectCompany(company)}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between"
                      >
                        <div>
                          <div className="font-medium text-slate-800 text-sm">{company.name}</div>
                          {company.industry && (
                            <div className="text-xs text-slate-500">{company.industry}</div>
                          )}
                        </div>
                        <span className="text-xs text-slate-400 font-mono">{company.company_code}</span>
                      </button>
                    ))}
                  </div>
                )}
                {selectedCompany && (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ Selected: {selectedCompany.name} ({selectedCompany.company_code})
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="skills" className="mb-1.5 block">
                  Key Skills (comma-separated)
                </Label>
                <Input
                  id="skills"
                  name="skills"
                  value={formData.skills}
                  onChange={handleChange}
                  placeholder="Python, JavaScript, React, Node.js, AWS"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="expected_salary" className="flex items-center gap-1 mb-1.5">
                    <DollarSign className="w-3.5 h-3.5" /> Expected Salary
                  </Label>
                  <Input
                    id="expected_salary"
                    name="expected_salary"
                    value={formData.expected_salary}
                    onChange={handleChange}
                    placeholder="15 LPA"
                  />
                </div>
                <div>
                  <Label htmlFor="notice_period_days" className="flex items-center gap-1 mb-1.5">
                    <Calendar className="w-3.5 h-3.5" /> Notice Period (days)
                  </Label>
                  <Input
                    id="notice_period_days"
                    name="notice_period_days"
                    type="number"
                    value={formData.notice_period_days}
                    onChange={handleChange}
                    placeholder="30"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="linkedin_url" className="flex items-center gap-1 mb-1.5">
                  <Linkedin className="w-3.5 h-3.5" /> LinkedIn Profile
                </Label>
                <Input
                  id="linkedin_url"
                  name="linkedin_url"
                  value={formData.linkedin_url}
                  onChange={handleChange}
                  placeholder="https://linkedin.com/in/yourprofile"
                />
              </div>

              <div>
                <Label htmlFor="professional_summary" className="mb-1.5 block">
                  Professional Summary
                </Label>
                <Textarea
                  id="professional_summary"
                  name="professional_summary"
                  value={formData.professional_summary}
                  onChange={handleChange}
                  placeholder="Brief description of your professional background and expertise..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="text-center">
            <Button
              type="submit"
              size="lg"
              disabled={submitting || (!cvFile && !formData.email)}
              className="px-12"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5 mr-2" />
                  Submit CV
                </>
              )}
            </Button>
            
            {!cvFile && !formData.email && (
              <p className="text-sm text-amber-600 mt-3 flex items-center justify-center gap-1">
                <AlertCircle className="w-4 h-4" />
                Please upload a CV or provide your email
              </p>
            )}
          </div>
        </form>

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 mt-8">
          Your information is secure and will only be used for recruitment purposes.
        </p>
      </div>
    </div>
  );
}
